import { UserState, Me } from '@/composables/User';
import { getUserProfile } from '@/composables/user/Profile';
import fetchShopInformation from '@/composables/shop/Shops';
import { fetchShopConfigurations } from '@/composables/shop/MyShopParameters';
import { checkIfDbCreated, checkIfShopConnected } from '@/configs/complete';
import { getOnboardingSteps, OnboardingStepId } from './useOnboardingState';

const isFilled = (value: unknown) => typeof value === 'string' && value.trim().length > 0;

const validateUserInfos = async (): Promise<boolean> => {
  try {
    await Me(false);
    const id = UserState.user?.id;
    if (!id) return false;
    const data: any = await getUserProfile(id);
    const u = data?.item ?? data?.data?.[0] ?? data?.[0] ?? data;
    if (!u) return false;
    return [
      u.first_name, u.last_name, u.phone_mobile, u.societe,
      u.rue, u.zip_code, u.city, u.country,
    ].every(isFilled);
  } catch {
    return false;
  }
};

const validateConnect = async (): Promise<boolean> => {
  try {
    return await checkIfShopConnected();
  } catch {
    return false;
  }
};

const validateShopConfirm = async (): Promise<boolean> => {
  try {
    const shopId = UserState.activeShop?.id ?? 0;
    if (!shopId) return false;
    const info = await fetchShopInformation({ shopId });
    if (!info) return false;
    if (!isFilled(info.name) || !info.idCategoryShop || !isFilled(info.currency) || !isFilled(info.defaultLang)) {
      return false;
    }
    const rgpd = await fetchShopConfigurations({ shopId }, 'shopRequiredDataRgpd');
    const hasPrivacy = rgpd?.some(
      (i: any) => i.key === 'shopRequiredDataRgpd_privacyPolicyUrl' && isFilled(i.value),
    );
    const hasDpo = rgpd?.some(
      (i: any) => i.key === 'shopRequiredDataRgpd_dataManagerEmail' && isFilled(i.value),
    );
    return Boolean(hasPrivacy && hasDpo);
  } catch {
    return false;
  }
};

const validateLiftOff = async (): Promise<boolean> => {
  try {
    return Boolean(await checkIfDbCreated());
  } catch {
    return false;
  }
};

const VALIDATORS: Record<OnboardingStepId, () => Promise<boolean>> = {
  userInfos: validateUserInfos,
  connect: validateConnect,
  shopConfirm: validateShopConfirm,
  liftOff: validateLiftOff,
};

export const resolveTargetIndex = async (persistedIndex: number): Promise<number> => {
  const steps = getOnboardingSteps();
  const max = Math.min(Math.max(persistedIndex, 0), steps.length - 1);
  for (let i = 0; i < max; i += 1) {
    const step = steps[i];
    // eslint-disable-next-line no-await-in-loop
    const ok = await VALIDATORS[step.id]();
    if (!ok) return i;
  }
  return max;
};

export const useOnboardingValidators = () => ({
  resolveTargetIndex,
  VALIDATORS,
});
